import { supabase } from './supabase';
import { ACCOUNTS } from './accounts';

// A JE number is an internal document — ledgers show the voucher behind a journal instead.
// resolveVoucherRefs(lines) takes journal_lines rows (each with journal_entries embedded and
// party_type/party_id) and returns Map(journal_entry_id → "RV-…" / "PV-…" text). Journals with
// no voucher behind them (manual journals) are simply absent — callers fall back to the JE.
//
// Deposit applications (DR Client Deposits / CR AR) reference the invoice, not a receipt: the
// deposit is a client-level pool. Their source receipt(s) are worked out FIFO from the client's
// 2010 credits — live receipts first, then voided ones, so an application still running on a
// voided receipt's money shows as "RV-… (VOID)" instead of hiding behind a JE number.

const RV_TYPES = ['RECEIPT_VOUCHER', 'CHEQUE_DEPOSIT', 'CHEQUE_RETURN', 'RECEIPT_VOID'];
const PV_TYPES = ['PAYMENT_VOUCHER'];

const chunks = (arr, n = 200) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));

async function fetchIn(table, select, col, ids, extra = (q) => q) {
  let out = [];
  for (const part of chunks(ids)) {
    const { data } = await extra(supabase.from(table).select(select).in(col, part));
    out = out.concat(data || []);
  }
  return out;
}

const label = (v) => v ? `${v.voucher_number}${['VOID', 'RETURNED'].includes(v.voucher_status) ? ` (${v.voucher_status})` : ''}` : null;

export async function resolveVoucherRefs(lines) {
  const out = new Map();
  const jes = (lines || []).map(l => l.journal_entries).filter(Boolean);

  const rvIds = [...new Set(jes.filter(je => RV_TYPES.includes(je.reference_type) && je.reference_id).map(je => je.reference_id))];
  const pvIds = [...new Set(jes.filter(je => PV_TYPES.includes(je.reference_type) && je.reference_id).map(je => je.reference_id))];
  const [rvs, pvs] = await Promise.all([
    rvIds.length ? fetchIn('receipt_vouchers', 'id, voucher_number, voucher_status', 'id', rvIds) : [],
    pvIds.length ? fetchIn('payment_vouchers', 'id, voucher_number, voucher_status', 'id', pvIds) : [],
  ]);
  const vById = new Map([...rvs, ...pvs].map(v => [v.id, v]));
  jes.forEach(je => { const v = vById.get(je.reference_id); if (v && [...RV_TYPES, ...PV_TYPES].includes(je.reference_type)) out.set(je.id, label(v)); });

  const clientIds = [...new Set((lines || [])
    .filter(l => l.journal_entries?.reference_type === 'DEPOSIT_APPLICATION' && l.party_type === 'client' && l.party_id)
    .map(l => l.party_id))];
  if (clientIds.length) {
    const sources = await depositApplicationSources(clientIds);
    sources.forEach((v, k) => out.set(k, v));
  }
  return out;
}

async function depositApplicationSources(clientIds) {
  const depLines = await fetchIn('journal_lines',
    'party_id, base_debit, base_credit, journal_entries!inner(id, entry_date, created_at, reference_type, reference_id, status, is_reversed, reversal_of)',
    'party_id', clientIds,
    q => q.eq('account_id', ACCOUNTS.CLIENT_DEPOSITS).eq('party_type', 'client').eq('journal_entries.status', 'POSTED'));

  const rvIds = [...new Set(depLines.filter(l => l.journal_entries.reference_type === 'RECEIPT_VOUCHER' && l.journal_entries.reference_id).map(l => l.journal_entries.reference_id))];
  const rvs = rvIds.length ? await fetchIn('receipt_vouchers', 'id, voucher_number, voucher_status', 'id', rvIds) : [];
  const rvById = new Map(rvs.map(r => [r.id, r]));

  const order = (a, b) => (a.journal_entries.entry_date || '').localeCompare(b.journal_entries.entry_date || '')
    || (a.journal_entries.created_at || '').localeCompare(b.journal_entries.created_at || '');
  const out = new Map();

  clientIds.forEach(cid => {
    const mine = depLines.filter(l => l.party_id === cid && !l.journal_entries.reversal_of).sort(order);
    const pool = (live) => mine
      .filter(l => Number(l.base_credit) > 0 && l.journal_entries.reference_type === 'RECEIPT_VOUCHER' && !!l.journal_entries.is_reversed === !live)
      .map(l => ({ rv: rvById.get(l.journal_entries.reference_id), left: Number(l.base_credit) }));
    const live = pool(true), voided = pool(false);
    mine.filter(l => l.journal_entries.reference_type === 'DEPOSIT_APPLICATION' && !l.journal_entries.is_reversed && Number(l.base_debit) > 0)
      .forEach(app => {
        let need = Number(app.base_debit);
        const used = [];
        for (const src of [...live, ...voided]) {
          if (need <= 0.01) break;
          if (src.left <= 0.01) continue;
          const take = Math.min(need, src.left);
          src.left -= take; need -= take;
          if (src.rv) used.push(label(src.rv));
        }
        if (used.length) out.set(app.journal_entries.id, [...new Set(used)].join(', '));
      });
  });
  return out;
}
